/**
 * Public "What's new" content, rendered by `src/pages/Changelog.tsx` at `/changelog`.
 *
 * This file is a checked-in snapshot of the curated, customer-facing entries
 * from the private `asktally/changelog` repo. It is written by the
 * `publish-public-changelog` skill in `asktally/ai-team`, never fetched at
 * runtime. The shape deliberately mirrors that repo's generated
 * `PUBLIC_CHANGELOG.md` so the sync is a mechanical reformat:
 *
 *   ## 2026-09-20              ->  ChangelogEntry.date
 *   ### Fixed                  ->  ChangelogSection.category
 *   - Some customer-facing...  ->  ChangelogSection.items (leading "- " dropped)
 *
 * The text here is English and is shown exactly as written. Do not edit entry
 * prose here; fix it in `asktally/changelog` and re-run the skill. Danish
 * translations live in the hand-maintained `./changelog.da.ts`, matched by
 * date; a date without a translation falls back to this English text. The
 * skill only rewrites the region between the `generated:entries` markers below;
 * the schema above them is hand-maintained (and must stay in sync with the
 * skill's category list).
 *
 * The `Internal` category is never published, so it is not part of the type.
 * Entry order does not matter; the page sorts newest first.
 */

export const CHANGELOG_CATEGORIES = [
  "Added",
  "Changed",
  "Fixed",
  "Removed",
  "Deprecated",
  "Security",
  "Performance",
] as const;

export type ChangelogCategory = (typeof CHANGELOG_CATEGORIES)[number];

export interface ChangelogSection {
  category: ChangelogCategory;
  /** One customer-facing sentence per item, plain text. */
  items: string[];
}

export interface ChangelogEntry {
  /** Merge date, ISO `YYYY-MM-DD` (UTC). */
  date: string;
  sections: ChangelogSection[];
}

// <generated:entries>
export const CHANGELOG_ENTRIES: ChangelogEntry[] = [
  {
    date: "2026-10-09",
    sections: [
      {
        category: "Added",
        items: [
          "In the conversation sidebar, a pulsing dot now marks each conversation Tally is still answering, and a solid dot marks one whose answer is ready.",
          "When the sidebar is closed, the button that opens it carries the same dot, so you can see an answer has arrived without opening the sidebar.",
        ],
      },
      {
        category: "Changed",
        items: [
          "The conversation sidebar is now open when you start Tally on a desktop, and stays as you left it instead of collapsing on every visit.",
          "On a tablet-sized screen, the sidebar no longer closes itself the moment you pick a conversation.",
          "Microsoft Business Central is now available to every Tally account — connect it from the Integrations page to ask Tally about your customers, vendors, invoices and finances.",
        ],
      },
    ],
  },
  {
    date: "2026-09-30",
    sections: [
      {
        category: "Changed",
        items: [
          "Tally has a new look: calmer, warmer colours with softer cards and buttons, and brown now marks what is selected instead of filling whole panels. Your conversations, dashboards and settings are unchanged.",
          "The menu button and page title now sit together in a slim bar at the top of the page.",
          "If you work with several companies, the company switcher now shows each company's initials and highlights the one you are in.",
        ],
      },
      {
        category: "Fixed",
        items: [
          "Dashboards, voice and Business Central, where enabled for your company, no longer stay hidden all session after a brief loading hiccup — Tally now retries on its own.",
        ],
      },
    ],
  },
];
// </generated:entries>
